import { PerStyleVariationMapBuilder } from "@faire/deprecated-retailer-visitor-shared/features/OpenSizing/PerStyleVariationMapBuilder";
import { isProductOptionInStock } from "@faire/deprecated-retailer-visitor-shared/lib/productOptions";
import { ProductAddToCartState } from "@faire/deprecated-retailer-visitor-shared/views/product/ProductDetails/AddToCart/useProductAddToCartState";
import { getSortedProductOptions } from "@faire/deprecated-retailer-visitor-shared/views/product/utils/productUtils";
import { IProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { IProductOption } from "@faire/web-api--source/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { IProductVariation } from "@faire/web-api--source/indigofair/data/IProductVariation";
import { IProductVariationMap } from "@faire/web-api--source/indigofair/data/IProductVariationMap";
import { ProductVariationType } from "@faire/web-api--source/indigofair/data/ProductVariationType";
import { logAssert } from "@faire/web/common/logging";
import { parseJson } from "@faire/web/common/string";
import cloneDeep from "lodash/cloneDeep";
import flatten from "lodash/flatten";
import groupBy from "lodash/groupBy";
import uniqBy from "lodash/uniqBy";
import { useCallback, useMemo, useState } from "react";

interface IVariantProps {
  value: string | undefined;
  type: keyof typeof ProductVariationType;
  display?: keyof typeof IProductVariationMap.IEntry.VariationDisplayType;
}

const VARIATION_TYPE_HIERARCHY: (keyof typeof ProductVariationType)[] = [
  ProductVariationType.COLOR,
  ProductVariationType.CUSTOM,
  ProductVariationType.SIZE,
];

const findVariantIndex = (variantType: keyof typeof ProductVariationType) => {
  const index = VARIATION_TYPE_HIERARCHY.indexOf(variantType);
  // this probably isn't needed since every variation will have a type
  // but will leave this here in case the backfill on prod isn't complete/safety precautions
  return index === -1 ? 1.5 : index;
};

const getVariationsFromVariationMap = (
  variationMap: Record<string, IVariantProps | undefined>
): IProductVariation[] =>
  Object.entries(variationMap).map(([name, data]) => ({
    name,
    value: data?.value,
    type: data?.type,
  }));

const getVariationKey = (variations?: IProductVariation[]): string =>
  JSON.stringify(
    variations?.slice().sort((variation1, variation2) => {
      logAssert(
        variation1.name !== undefined && variation2.name !== undefined,
        "variations always have a name property",
        { data: { variation1, variation2 } }
      );
      if (variation1.name === variation2.name) {
        return 0;
      }
      return variation1.name > variation2.name ? 1 : -1;
    })
  );

const getVariationsFromVariationKey = (
  variationKey: string
): IProductVariation[] => {
  try {
    const parsedVariations = parseJson(variationKey);
    if (Array.isArray(parsedVariations)) {
      return parsedVariations;
    }
  } catch {
    // do nothing
  }
  return [];
};

export type VariantOptionSelectionViewState = ReturnType<
  typeof useVariantOptionSelectionViewState
>;

export const useVariantOptionSelectionViewState = (
  product: IProductPageResponse.IPdpProduct,
  productAddToCartState: ProductAddToCartState
) => {
  const [selectedVariations, setSelectedVariations] = useState(() => {
    const variations: { [variantName: string]: IVariantProps } = {};
    productAddToCartState.selectedOption?.variations?.forEach(
      (v) =>
        (variations[v.name!] = {
          value: v.value,
          type: v.type!,
        })
    );
    return variations;
  });

  const options = useMemo(() => {
    return getSortedProductOptions(product);
  }, [product]);

  const optionsMapByVariationKey = useMemo(() => {
    return options.reduce(
      (optionMap, option) => {
        optionMap[getVariationKey(option.variations)] = option;
        return optionMap;
      },
      {} as Record<string, IProductOption>
    );
  }, [options]);

  const optionBasedOnSelectedVariations = useMemo(() => {
    return optionsMapByVariationKey[
      getVariationKey(getVariationsFromVariationMap(selectedVariations))
    ];
  }, [optionsMapByVariationKey, selectedVariations]);

  const updateOption = useCallback(
    (variations: typeof selectedVariations) => {
      const optionBasedOnSelectedVariations =
        optionsMapByVariationKey[
          getVariationKey(getVariationsFromVariationMap(variations))
        ];
      productAddToCartState.updateSelectedOption(
        optionBasedOnSelectedVariations
      );
    },
    [optionsMapByVariationKey, productAddToCartState]
  );

  const getSelectedVariationValue = useCallback(
    (variationName: string): undefined | string =>
      selectedVariations[variationName]?.value,
    [selectedVariations]
  );

  const hasValidOptionBasedOnSelectedVariations = useMemo(() => {
    return optionBasedOnSelectedVariations !== undefined;
  }, [optionBasedOnSelectedVariations]);

  const allVariations = useMemo(() => {
    return uniqBy(
      flatten(options.map((option) => option.variations ?? [])),
      (variation) => JSON.stringify(variation)
    );
  }, [options]);

  const variationsGroupedByName = useMemo(() => {
    return groupBy(allVariations, ({ name }) => name);
  }, [allVariations]);

  const variationTypes: IVariantProps[] = useMemo(() => {
    const variationKeysByName = Object.keys(variationsGroupedByName);
    const variationPropsByName = variationKeysByName.map((variationKey) => ({
      value: variationKey,
      type:
        variationsGroupedByName[variationKey]?.[0]?.type ??
        ProductVariationType.CUSTOM,
    }));
    return variationPropsByName.sort(
      (variationA: IVariantProps, variationB: IVariantProps) => {
        return (
          findVariantIndex(variationA.type) - findVariantIndex(variationB.type)
        );
      }
    );
  }, [variationsGroupedByName]);

  const allOptionsHaveSwatches = useCallback(
    (variationName: string) => {
      const entryValues =
        product.purchase_information?.variation_map?.entries.find(
          (entry) => entry.name === variationName
        )?.entry_values;
      return (entryValues?.length ?? 0) > 0
        ? (entryValues?.every((entryValue) => entryValue.image !== undefined) ??
            false)
        : false;
    },
    [product.purchase_information?.variation_map?.entries]
  );

  const styleVariationTypes = useMemo(() => {
    return variationTypes.filter(
      (variation) => variation.type !== ProductVariationType.SIZE
    );
  }, [variationTypes]);

  const isStyleVariationType = useCallback(
    (name: string): boolean =>
      styleVariationTypes.some((variationType) => variationType.value === name),
    [styleVariationTypes]
  );

  const primaryVariation = useMemo(() => {
    const primaryVariationName = variationTypes[0]?.value;
    if (primaryVariationName === undefined) {
      return undefined;
    }
    const primaryVariationValue =
      getSelectedVariationValue(primaryVariationName);
    return IProductVariation.build({
      name: primaryVariationName,
      value: primaryVariationValue,
    });
  }, [getSelectedVariationValue, variationTypes]);
  const doesVariationKeyContainVariation = useCallback(
    (variationKey: string, variation: IProductVariation) =>
      variationKey.includes(JSON.stringify(variation).slice(1, -1)),
    []
  );

  const findAvailableVariationKey = useCallback(
    (selectedVariation: IProductVariation) => {
      const currentPrimaryVariation = primaryVariation;
      if (currentPrimaryVariation === undefined) {
        return undefined;
      }
      // when selected variation is the primary variation,
      // ignore current primary variation and check
      // whether some variation key contains selected variation
      if (selectedVariation.name === currentPrimaryVariation.name) {
        return Object.keys(optionsMapByVariationKey).find((variationKey) =>
          doesVariationKeyContainVariation(variationKey, selectedVariation)
        );
      }
      // when selected variation is not the primary variation,
      // apply primary variation and check
      // whether some variation key contains both variations
      return Object.keys(optionsMapByVariationKey).find(
        (variationKey) =>
          doesVariationKeyContainVariation(variationKey, selectedVariation) &&
          doesVariationKeyContainVariation(
            variationKey,
            currentPrimaryVariation
          )
      );
    },
    [
      doesVariationKeyContainVariation,
      optionsMapByVariationKey,
      primaryVariation,
    ]
  );

  const findInStockVariationKey = useCallback(
    (selectedVariation: IProductVariation) => {
      const currentPrimaryVariation = primaryVariation;
      if (currentPrimaryVariation === undefined) {
        return undefined;
      }
      // when selected variation is the primary variation,
      // ignore current primary variation and check
      // whether some variation key contains selected variation
      // and the option is FOR_SALE
      if (selectedVariation.name === currentPrimaryVariation.name) {
        return Object.keys(optionsMapByVariationKey).find(
          (variationKey) =>
            doesVariationKeyContainVariation(variationKey, selectedVariation) &&
            optionsMapByVariationKey[variationKey]?.state ===
              IProduct.State.FOR_SALE
        );
      }
      // when selected variation is not the primary variation,
      // apply primary variation and check
      // whether some variation key contains both variations
      // and the option is FOR_SALE
      return Object.keys(optionsMapByVariationKey).find(
        (variationKey) =>
          doesVariationKeyContainVariation(variationKey, selectedVariation) &&
          doesVariationKeyContainVariation(
            variationKey,
            currentPrimaryVariation
          ) &&
          optionsMapByVariationKey[variationKey]?.state ===
            IProduct.State.FOR_SALE
      );
    },
    [
      doesVariationKeyContainVariation,
      optionsMapByVariationKey,
      primaryVariation,
    ]
  );

  const isAlternateOpenSizingProduct = useCallback(
    (productOption: IProductOption | undefined): boolean =>
      productAddToCartState.isOptionPartOfOpenSizing(productOption),
    [productAddToCartState]
  );

  const buildAlternateOpenSizingVariationMap = useCallback(
    (
      productOption: IProductOption | undefined
    ): PerStyleVariationMapBuilder.IPerStyleOptionVariation[] | undefined => {
      if (
        productOption === undefined ||
        !isAlternateOpenSizingProduct(productOption)
      ) {
        return undefined;
      }
      return PerStyleVariationMapBuilder.buildVariationMap(
        productOption.token!,
        product
      );
    },
    [isAlternateOpenSizingProduct, product]
  );

  /**
   * @param variation the variation to be selected
   * @returns state of selected variation: FOR_SALE when in stock, SALE_PAUSED when out of stock, undefined when unavailable
   */
  const stateForAlternateVariation = useCallback<
    (variation: IProductVariation) => keyof typeof IProduct.State | undefined
  >(
    (variation: IProductVariation) => {
      if (variation.name === undefined || variation.value === undefined) {
        return undefined;
      }
      if (!isStyleVariationType(variation.name)) {
        const variationMap = cloneDeep(selectedVariations);
        const thisVariation = variationMap[variation.name];
        if (thisVariation) {
          thisVariation.value = variation.value;
        }
        const option =
          optionsMapByVariationKey[
            getVariationKey(getVariationsFromVariationMap(variationMap))
          ];

        return option?.state;
      }
      const availableKey = findAvailableVariationKey(variation);
      if (availableKey === undefined) {
        return undefined;
      }
      const inStockKey = findInStockVariationKey(variation);
      if (inStockKey === undefined) {
        return IProduct.State.SALES_PAUSED;
      }
      return IProduct.State.FOR_SALE;
    },
    [
      findAvailableVariationKey,
      findInStockVariationKey,
      isStyleVariationType,
      optionsMapByVariationKey,
      selectedVariations,
    ]
  );

  /**
   * @param variation the variation to be selected
   * @returns an option with specified variation and the primary variation selected, returns undefined if such option does not exist
   */
  const optionForAlternateVariation = useCallback<
    (variation: IProductVariation) => IProductOption | undefined
  >(
    (variation: IProductVariation) => {
      if (variation.name === undefined || variation.value === undefined) {
        return undefined;
      }

      if (!isStyleVariationType(variation.name)) {
        const variationMap = cloneDeep(selectedVariations);
        const thisVariation = variationMap[variation.name];
        if (thisVariation) {
          thisVariation.value = variation.value;
        }
        return optionsMapByVariationKey[
          getVariationKey(getVariationsFromVariationMap(variationMap))
        ];
      }

      const key = findAvailableVariationKey(variation);

      if (key === undefined) {
        return undefined;
      }

      return optionsMapByVariationKey[key];
    },
    [
      findAvailableVariationKey,
      isStyleVariationType,
      optionsMapByVariationKey,
      selectedVariations,
    ]
  );

  const isVariationOutOfStock = useCallback(
    (variation: IProductVariation) => {
      const productOption = optionForAlternateVariation(variation);
      const alternateOpenSizingVariationMap =
        buildAlternateOpenSizingVariationMap(productOption);
      if (productOption && alternateOpenSizingVariationMap) {
        return !isProductOptionInStock(
          productOption,
          alternateOpenSizingVariationMap
        );
      } else {
        return (
          stateForAlternateVariation(variation) !== IProduct.State.FOR_SALE
        );
      }
    },
    [
      buildAlternateOpenSizingVariationMap,
      optionForAlternateVariation,
      stateForAlternateVariation,
    ]
  );

  const handleVariationSelect = useCallback(
    (name: string, value: string) => {
      let newSelectedVariations = selectedVariations;
      const variation = selectedVariations[name];
      if (variation) {
        newSelectedVariations = {
          ...selectedVariations,
          [name]: {
            ...variation,
            value,
          },
        };
      }
      const optionBasedOnSelectedVariations =
        optionsMapByVariationKey[
          getVariationKey(getVariationsFromVariationMap(newSelectedVariations))
        ];

      if (optionBasedOnSelectedVariations === undefined) {
        // when selected variations has no available options,
        // switch to an available option with primary variation unchanged
        const variationKey = findAvailableVariationKey(
          IProductVariation.build({
            name,
            value,
          })
        );
        const variations = getVariationsFromVariationKey(
          variationKey ??
            getVariationKey(
              getVariationsFromVariationMap(newSelectedVariations)
            )
        );
        variations?.forEach((variation) => {
          if (variation.name !== undefined && variation.type !== undefined) {
            newSelectedVariations[variation.name] = {
              value: variation.value,
              type: variation.type,
            };
          }
        });
      }
      setSelectedVariations(newSelectedVariations);
      updateOption(newSelectedVariations);
    },
    [
      findAvailableVariationKey,
      optionsMapByVariationKey,
      selectedVariations,
      updateOption,
    ]
  );

  const isAlternateOpenSizingAvailable = useCallback(
    (productOption: IProductOption | undefined): boolean => {
      const variationMap = buildAlternateOpenSizingVariationMap(productOption);
      return !!variationMap?.some((variation) => variation.inventory > 0);
    },
    [buildAlternateOpenSizingVariationMap]
  );

  return useMemo(
    () => ({
      product,
      getSelectedVariationValue,
      hasValidOptionBasedOnSelectedVariations,
      variationsGroupedByName,
      variationTypes,
      allOptionsHaveSwatches,
      isAlternateOpenSizingProduct,
      buildAlternateOpenSizingVariationMap,
      isVariationOutOfStock,
      handleVariationSelect,
      currentStyleHasOnlyPrePacks:
        productAddToCartState.currentStyleHasOnlyPrePacks,
      styleVariationTypes,
      isAlternateOpenSizingAvailable,
      stateForAlternateVariation,
      optionForAlternateVariation,
    }),
    [
      product,
      getSelectedVariationValue,
      hasValidOptionBasedOnSelectedVariations,
      variationsGroupedByName,
      variationTypes,
      allOptionsHaveSwatches,
      isAlternateOpenSizingProduct,
      buildAlternateOpenSizingVariationMap,
      isVariationOutOfStock,
      handleVariationSelect,
      productAddToCartState.currentStyleHasOnlyPrePacks,
      styleVariationTypes,
      isAlternateOpenSizingAvailable,
      stateForAlternateVariation,
      optionForAlternateVariation,
    ]
  );
};
